/**
 * Latvian catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `dir: 'ltr'`. Native-grade Latvian at a mathematician's register. **Warrant → «Pamatojums»**
 * (justification — flagged). W/M grades stay Latin (`::numbering-system/latn`). CLDR cardinal
 * categories are zero / one / other (the `zero` category covers 0 and numbers ending in 0 or
 * 11–19, taking the genitive «ierobežojumu»; `one` is 1, 21, … taking «ierobežojums»), so
 * `frameActive` declares exactly those (plus an explicit `=0` for the nicer empty phrasing).
 * Per INV-STABLE-ID a wrong translation is a presentation concern only — verdicts stay fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const LV_CATALOG = {
    locale: 'lv',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Izpēte',
        [P2.sectionClaims]: 'Apgalvojumi',
        [P2.sectionIntegrity]: 'Integritāte',
        [P2.axisWarrantLabel]: 'Pamatojums',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empīrisks',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definīcija} interp {Interpretācija} testable {Pārbaudāms} tested {Pārbaudīts} refuted {Atspēkots} program {Pētniecības programma} philos {Filozofisks} other {Nezināms}}',
        [P2.axisModalLabel]: 'Modāls',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Stipruma līmenis {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Izpētāms',
        [P2.stateRefuted]: 'Atspēkots',
        [P2.stateOutside]: 'Ārpus tvēruma',
        [P2.verdictConflict]: 'Konfliktā ar {constraints}',
        [P2.verdictOutside]: 'Nepieciešams {missing}',
        [P2.lossRegisterPlain]: 'Vienkāršots ikdienas valodā — daļa precizitātes ir izlaista.',
        [P2.lossRegisterKid]: 'Vienkāršots jaunākiem lasītājiem — daudzas detaļas ir izlaistas.',
        [P2.lossProfileDetail]: 'Pilnais profils Pamatojums / Empīrisks / Modāls ir apkopots šeit.',
        [P2.lossConflictDetail]: 'Konflikta detaļas šajā skatā ir saīsinātas.',
        [P2.laneVerified]: 'Verificēts',
        [P2.laneUnverified]: 'Neverificēts',
        [P2.laneConflict]: 'Konflikts pierādīts',
        [P2.constraintLogic]: 'Loģika',
        [P2.constraintLightBends]: 'Gaisma izliecas gravitācijas laukā',
        [P2.constraintParallelPostulate]: 'Paralēļu postulāts',
        [P2.claimScalarGravity]: 'Skalārā gravitācijas teorija',
        [P2.claimNonEuclidean]: 'Neeiklīda ģeometrija',
        [P2.claimSubstrateSr]: 'Speciālās relativitātes substrāta interpretācija',
        [P2.edgeExtObservation]: 'Ārējs novērojums',
        [P2.edgeResourceSample]: 'Koplietots skaitļošanas resurss',
        [P2.edgeHeuristicSample]: 'Heiristisks norādījums',
        [P2.edgeSharedSample]: 'Kopīgs pamats',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Ierobežojumi',
        [P2.sectionEdges]: 'Šķautnes',
        [P2.sectionFrame]: 'Ietvars',
        [P2.sectionExposure]: 'Pakļautība',
        [P2.statusAdoptedFormal]: 'Pieņemts (formāls)',
        [P2.statusAdoptedInterpretive]: 'Pieņemts (interpretatīvs)',
        [P2.statusSuspended]: 'Apturēts',
        [P2.edgeEffectConstraint]: 'Veido ierobežojumu (var atspēkot)',
        [P2.edgeEffectResource]: 'Resurss — neietekmē spriedumus',
        [P2.edgeEffectHeuristic]: 'Heiristika — neietekmē spriedumus',
        [P2.edgeEffectShared]: 'Kopīgs pamats — neietekmē spriedumus',
        [P2.frameActive]: '{count, plural, =0 {Nav aktīvu ierobežojumu} zero {# aktīvu ierobežojumu} one {# aktīvs ierobežojums} other {# aktīvi ierobežojumi}}',
        [P2.exposureNone]: 'Nav interpretatīvu atkarību',
        [P2.exposureDep]: 'Atkarīgs no {id}',
        [P2.laneTrackMachine]: 'Mašīna',
        [P2.laneTrackExpert]: 'Eksperts',
        [P2.laneTrackCommunity]: 'Kopiena',
        [P2.expertAudited]: 'Auditēts',
        [P2.expertInProgress]: 'Notiek',
        [P2.expertNotReviewed]: 'Nav pārskatīts',
        [P2.communityFlags]: 'Ir atzīmes',
        [P2.communityNone]: 'Nav atzīmju',
        [P2.controlChallenge]: 'Apstrīdēt',
        [P2.controlActivate]: 'Aktivizēt',
        [P2.controlMakeInterpretive]: 'Padarīt interpretatīvu',
        [P2.controlImportEdge]: 'Importēt šķautni',
        [P2.controlPromote]: 'Paaugstināt',
        [P2.controlUndo]: 'Atsaukt',
        [P2.controlRedo]: 'Atkārtot',
        [P2.controlRegister]: 'Reģistrēt',
        [P2.promoteRejected]: 'Paaugstināšana noraidīta',
        [P2.promoteAccepted]: 'Paaugstināšana pieņemta',
        [P2.promoteReasonRefutationTerminal]: 'Pierādījums nevar dziedēt atspēkojumu.',
        [P2.promoteReasonKeystone]: 'Stūrakmens paaugstināšanai nepieciešams empīrisks atbalsts.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Empīriskais veids nav fiksēts.',
        [P2.promoteReasonModalNotFixed]: 'Modālā pakāpe nav fiksēta.',
        [P2.promoteReasonWarrantNotImproved]: 'Pamatojuma pakāpe nav uzlabota.',
        [P2.evidenceComputation]: "{engine} aprēķināja {op} = {result} ar {config}",
        [P2.lossNumericRounding]: "Rezultāts noapaļots līdz {dp} cipariem aiz komata",
    },
};
//# sourceMappingURL=lv.js.map